import React, { useState, useRef, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import api from '../utils/api';
import useAuth from '../context/useAuth';
import ReCAPTCHA from 'react-google-recaptcha';

const maskEmail = (email) => {
  const [localPart, domain] = email.split('@');
  const maskedLocal = localPart.slice(0, 4) + '****';
  return `${maskedLocal}@${domain}`;
};

const maskPhone = (phone) => {
  const firstPart = phone.slice(0, 3);
  const lastPart = phone.slice(-4);
  return `${firstPart}****${lastPart}`;
};

const Login = () => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false); // New state for showing/hiding passwords
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpMode, setOtpMode] = useState('email');
  const [otpRequired, setOtpRequired] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const { login } = useAuth();
  const history = useHistory();
  const usernameRef = useRef(null);

  useEffect(() => {
    usernameRef.current.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/login', { name, password, recaptchaToken });
      if (response.data.isOTPRequired) {
        setOtpRequired(true);
        setMaskedEmail(maskEmail(response.data.email));
        setMaskedPhone(maskPhone(response.data.phone));
      } else {
        loginUser(response);
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setError('Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/verify-otp', { name, code: otpCode });
      loginUser(response);
    } catch (error) {
      console.error('Failed to verify OTP:', error);
      setError('Invalid OTP code. Please try again.');
    }
  };

  const sendOtp = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/api/send-otp', { name, method: otpMode });
      setCodeSent(true);
    } catch (error) {
      console.error('Failed to send OTP:', error);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loginUser = (response) => {
    login(response.data.token, response.data.role, response.data.expiryTime, response.data.umemid, response.data.username, response.data.resultsPerPage);
    const mustChangePassword = response.data.mustChangePassword;
    if (mustChangePassword) {
      history.push('/reset-password');
    } else {
      history.push('/');
    }
  };

  const onReCAPTCHAChange = (token) => {
    setRecaptchaToken(token);
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title text-center">Login</h3>
            </div>
            <div className="card-body">
              {!otpRequired ? (
                <form onSubmit={handleSubmit} aria-live="assertive">
                  <div className="form-group">
                    <label htmlFor="username">Username: <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      id="username"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="username"
                      ref={usernameRef}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">Password: <span className="text-danger">*</span></label>
                    <div className="d-flex align-items-center">
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        id="password"
                        className="form-control flex-grow-1 mr-2"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                        autoComplete="current-password"
                      />
                      <div className="form-check ml-2 d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          id="showPasswords"
                          className="form-check-input"
                          checked={showPasswords}
                          onChange={() => setShowPasswords(!showPasswords)}
                        />
                        <label className="form-check-label ml-2" htmlFor="showPasswords">Show Password</label>
                      </div>
                    </div>
                  </div>
                  {error && <div className="text-danger mb-3" role="alert">{error}</div>}
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={onReCAPTCHAChange}
                  />
                  <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                    {loading ? 'Logging in...' : 'Login'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleOtpSubmit} aria-live="assertive">
                  <div className="form-group">
                    <p>Let's verify your identity. Your data security is our priority.</p>
                    <label>Where can we send your one-time PIN (OTP)?</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="otpEmail"
                        name="otpOption"
                        value="email"
                        onChange={(e) => setOtpMode(e.target.value)}
                        disabled={codeSent}
                        checked={otpMode === 'email'}
                      />
                      <label className="form-check-label" htmlFor="otpEmail">
                        Email: {maskedEmail}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="otpPhone"
                        name="otpOption"
                        value="phone"
                        onChange={(e) => setOtpMode(e.target.value)}
                        disabled
                      />
                      <label className="form-check-label" htmlFor="otpPhone">
                        Phone: {maskedPhone} (Coming Soon)
                      </label>
                    </div>
                  </div>
                  <button type="button" className="btn btn-primary btn-block" onClick={sendOtp} disabled={loading || codeSent}>
                    {loading ? 'Sending OTP...' : codeSent ? 'OTP Sent' : 'Send OTP'}
                  </button>
                  {codeSent && (
                    <>
                      <div className="form-group">
                        <label htmlFor="otpCode">Enter the OTP code sent to your {otpMode}:</label>
                        <input
                          type="text"
                          id="otpCode"
                          className="form-control"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </div>
                      <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                        {loading ? 'Verifying...' : 'Verify OTP'}
                      </button>
                    </>
                  )}
                  {error && <div className="text-danger mb-3" role="alert">{error}</div>}
                </form>
              )}
            </div>
          </div>
          {!otpRequired && (
            <>
              <p className="mt-3 text-center">
                Don't have an account? <Link to="/register">Register here</Link>
              </p>
              <p className="mt-3 text-center">
                Forgot Password? <Link to="/forgot-password">Request temporary password here</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;